import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Button,
  Paper,
  Box,
  TextField,
  CircularProgress
} from "@material-ui/core";
import { useFormik } from "formik";
import RawBreadCrumbs from "@/components/BreadCrumbs";
import { useTranslation } from "@/langs/useTranslation";
import StripedTitle from "@/components/StripedTitle";
import { FormItem } from "@/components/Form";
import DuringExecute from "./reduce/DuringExecute";
import LoopMode from "./reduce/LoopMode";
import ExecuteTime from "./reduce/ExecuteTime";
import DisplayContent from "./reduce/DisplayContent";
import * as Yup from "yup";
import { fullWindowTemplateId, pldmTemplateId } from "./CONSTANT";
import {
  createCMSTemplate,
  editCMSTemplate,
  getTemplateDetail
} from "@/apis/apiCollection";
import { useHistory } from "react-router-dom";
import { toast } from "@/apis/request";
import { useParams } from "react-router-dom";
import ContentPicker from "./reduce/ContentPicker";
import MediaPreview from "../MediaPreview";

const CMSPLDMTemplate = () => {
  const { t, userLanguage } = useTranslation();
  const { id: templateId, type = "create" } = useParams();
  const [loading, setLoading] = useState(false);
  const readonly = type === "view";
  const history = useHistory();

  const initialValues = {
    name: "",
    effectType: 1,
    effectBeginTime: 0,
    effectEndTime: 0,
    effectTimes: 0,
    contentTemplateTypeId: pldmTemplateId,
    contentTemplateDetail: {
      pictureAbscissa: 0,
      pictureOrdinate: 0,
      pictureWidth: 0,
      pictureHeight: 0,
      videoAbscissa: 0,
      videoOrdinate: 0,
      videoWidth: 0,
      videoHeight: 0,
      pictureResource: {},
      videoResource: {},
    }
  };

  const { values, handleChange, handleSubmit, touched, errors, setValues } =
    useFormik({
      initialValues,
      onSubmit: async () => {
        // 图片和视频必选其一
        if (!values.contentTemplateDetail.pictureResource.resourceId &&
          !values.contentTemplateDetail.videoResource.resourceId
        ) {
          toast.error(t({ defaultMessage: "必须选择一个图片或视频" }))
          return
        }
        const newValue = {
          ...values
        }
        const { contentTemplateDetail } = newValue
        newValue.contentTemplateDetail = {
          ...contentTemplateDetail,
          pictureAbscissa: Number(contentTemplateDetail.pictureAbscissa),
          pictureOrdinate: Number(contentTemplateDetail.pictureOrdinate),
          pictureWidth: Number(contentTemplateDetail.pictureWidth),
          pictureHeight: Number(contentTemplateDetail.pictureHeight),
          videoAbscissa: Number(contentTemplateDetail.videoAbscissa),
          videoOrdinate: Number(contentTemplateDetail.videoOrdinate),
          videoWidth: Number(contentTemplateDetail.videoWidth),
          videoHeight: Number(contentTemplateDetail.videoHeight),
        }
        newValue.contentTemplateDetail = JSON.stringify(
          newValue.contentTemplateDetail
        );
        if (type === "create") await createCMSTemplate(newValue);
        if (type === "edit") {
          newValue.id = templateId;
          await editCMSTemplate(newValue);
        }
        history.goBack();
        toast.success(t({ defaultMessage: "创建成功" }));
      },
      validationSchema: Yup.object({
        name: Yup.string().required("必填项"),
        // contentTemplateDetail: Yup.object({
        // })
      })
    });

  const BreadCrumbs = useMemo(
    () =>
      RawBreadCrumbs([
        {
          name: t({ defaultMessage: "内容模板列表" }),
          to: "/cms/template/list"
        }
      ]),
    []
  );

  const pageName = useMemo(() => {
    switch (type) {
      case "create":
        return t({ defaultMessage: "新建模板" });
      case "edit":
        return t({ defaultMessage: "编辑模版" });
      case "view":
        return t({ defaultMessage: "预览模板" });
      default:
    }
  }, [userLanguage, type]);

  useEffect(() => {
    if (type != "create" && templateId) {
      setLoading(true);
      getTemplateDetail({
        id: templateId
      }).then(res => {
        try {
          const detail = JSON.parse(res.contentTemplateDetail);
          setValues({
            name: res.name,
            effectType: 1,
            effectBeginTime: 0,
            effectEndTime: 0,
            effectTimes: 0,
            contentTemplateTypeId: pldmTemplateId,
            contentTemplateDetail: detail
          });
        } catch (e) {
        } finally {
          setLoading(false);
        }
      });
    }
  }, [type, templateId]);

  return (
    <div>
      <BreadCrumbs pageName={pageName} />
      <Paper elevation={0}>
        <form style={{ flex: 1 }} onSubmit={handleSubmit}>
          <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
            <Typography variant="h6">{pageName}</Typography>
            {!readonly && (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginLeft: "auto" }}
              >
                {t({ defaultMessage: "确定" })}
              </Button>
            )}
            <Button
              variant="outlined"
              // type="submit"
              style={{ marginLeft: readonly ? "auto" : 16 }}
              onClick={() => {
                history.goBack();
              }}
            >
              {t({ defaultMessage: "取消" })}
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              style={{ marginTop: 50 }}
            >
              <div style={{ width: 1000 }}>
                <StripedTitle>{t({ defaultMessage: "基本信息" })}</StripedTitle>
                <Box
                  display="flex"
                  flexDirection="row"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    style={{ flex: 1 }}
                  >
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "内容名称" })}
                        </Typography>
                      }
                      required
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="name"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.name}
                        size="small"
                        // inputProps={{ maxLength: 30 }}
                        disabled={readonly}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "图片横坐标" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.pictureAbscissa"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.pictureAbscissa}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "图片纵坐标" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.pictureOrdinate"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.pictureOrdinate}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "图片宽度" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.pictureWidth"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.pictureWidth}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "图片高度" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.pictureHeight"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.pictureHeight}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "图片文件（*.jpg/*.png）" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <ContentPicker
                        name="contentTemplateDetail.pictureResource"
                        value={values.contentTemplateDetail.pictureResource}
                        handleChange={handleChange}
                        readonly={readonly}
                        type="image"
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "视频横坐标" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.videoAbscissa"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.videoAbscissa}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "视频纵坐标" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.videoOrdinate"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.videoOrdinate}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "视频宽度" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.videoWidth"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.videoWidth}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "视频高度" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <TextField
                        variant="outlined"
                        name="contentTemplateDetail.videoHeight"
                        onChange={handleChange}
                        style={{ width: "100%", background: "#fff" }}
                        value={values.contentTemplateDetail.videoHeight}
                        size="small"
                        disabled={readonly}
                      />
                    </FormItem>
                    <FormItem
                      label={
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: "bold" }}
                        >
                          {t({ defaultMessage: "视频文件（*.mp4/*.mov）" })}
                        </Typography>
                      }
                      row
                      colon={false}
                    >
                      <ContentPicker
                        name="contentTemplateDetail.videoResource"
                        value={values.contentTemplateDetail.videoResource}
                        handleChange={handleChange}
                        readonly={readonly}
                        type="video"
                      />
                    </FormItem>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    style={{ flex: 1, marginLeft: 80 }}
                  >
                    {values.contentTemplateDetail?.pictureResource && !!values.contentTemplateDetail.pictureResource.s3Path && (
                      <MediaPreview type="image" s3Path={values.contentTemplateDetail.pictureResource.s3Path} style={{ width: 335, height: "auto", borderRadius: 6, objectFit: "contain" }} />
                    )}
                    {values.contentTemplateDetail?.videoResource && !!values.contentTemplateDetail.videoResource.s3Path && (
                      <MediaPreview type="video" s3Path={values.contentTemplateDetail.videoResource.s3Path} style={{ width: 335, height: "auto", borderRadius: 6, objectFit: "contain" }} />
                    )}
                  </Box>
                </Box>

              </div>
            </Box>
          )}
        </form>
      </Paper>
    </div>
  );
};

export default CMSPLDMTemplate;
